import React, { useEffect, useState } from "react";
import { RouteComponentProps, useLocation } from "react-router";
import {
  Grid,
  Typography,
  Paper,
  Button,
  makeStyles,
  createStyles,
  Theme,
  Card,
  CardHeader,
  Divider,
  CardContent,
} from "@material-ui/core";
import Box from "@material-ui/core/Box";
import { PictureAsPdf } from "@material-ui/icons";
import useStudentDataInfo from "../../hooks/student-data-info/useStudentDataInfo";
import TrTable from "../shared/controls/TrTable";
import TrTableHeadMetadata from "../../models/shared/TrTableHeadMetadata";
import TrTableBodyData from "../../models/shared/TrTableBodyData";
import PaymentRequestDetails from "../../models/payment-request/PaymentRequestDetails";
import PaymentRequestStudentDetails from "../../models/payment-request/PaymentRequestStudentDetails";
import PaymentRequestSchoolCarrierDetails from "../../models/payment-request/PaymentRequestSchoolCarrierDetails";
import PaymentRequestAuthorizedCarrierDetails from "../../models/payment-request/PaymentRequestAuthorizedCarrierDetails";
import PaymentRequestApiClient from "../../api/PaymentRequestApiClient";
import { numberFormat, currencyFormat, isSpecialCalculationFor2023Date } from "../../utilities/utilities";
import { paymentRequestPrintUrl } from "../../urls";
import PaymentRequestStatusElement from "../shared/controls/PaymentRequestStatusElement";
import Currency from "../../models/shared/Currency";
import AnnualAdministration from "../../models/admin/annual-administration/AnnualAdministration";
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { Link } from "react-router-dom";


interface MatchParams {
  id: string;
}

interface Props extends RouteComponentProps<MatchParams> { }

export default function PaymentRequestDetailsPage({ match }: Props) {
  const classes = useStyles({});
  const location = useLocation();
  const { id } = match.params;
  const apiClient = new PaymentRequestApiClient();

  const [paymentRequest, setPaymentRequest] = useState<PaymentRequestDetails>(
    {} as PaymentRequestDetails
  );

  const [annualAdministration, setAnualAdministration] = useState<
    AnnualAdministration
  >({} as AnnualAdministration);

  useEffect(() => {
    async function getPaymentRequest() {
      const request = await apiClient.getDetails(id);
      const pricingRequest = await apiClient.getAnualPricing();
      setAnualAdministration(pricingRequest.filter((price) => {
        return price.schoolYearName === request.schoolYearName;
      })[0]);
      setPaymentRequest(request);
    }

    getPaymentRequest();
  }, []);

  const {
    getTransportOptionInfoByStudentId,
    getFoodOptionInfoByStudentId,
    getTeachingAidOptionInfoByStudentId,
  } = useStudentDataInfo({ paymentRequestId: paymentRequest.id });

  const handleDownload = async () => {
    await apiClient.exportDetails(id);
  };

  const generateInfo = (
    info: string,
    numberOfDays: number,
    currency: Currency,
    cost?: number,
    correctedCost?: number

  ) => {
    return `${info == "-" ? "" : info + ", "
      } Broj dana: ${numberOfDays}, Cijena: ${currencyFormat(cost || 0, currency)} (${currencyFormat(correctedCost || 0, currency)})`;
  };

  const tableHeadMetadata: TrTableHeadMetadata<
    PaymentRequestStudentDetails
  >[] = [
      {
        id: "student",
        label: "Učenik",
        dataFunc: (d: PaymentRequestStudentDetails) => (
          <>
            <Typography variant="body2">
              {d.lastName + " " + d.firstName}
            </Typography>
            <Typography variant="body2">{d.birthDate}</Typography>
          </>
        ),
      },
      { id: "oib", label: "OIB", dataName: "oib" },
      {
        id: "schoolClass",
        label: "Razred",
        dataFunc: (d: PaymentRequestStudentDetails) => (
          <>
            <Typography variant="body2">{d.programName}</Typography>
            <Typography variant="body2">
              {d.schoolClass + " - " + d.sectionMark}
            </Typography>
          </>
        ),
      },
      {
        id: "vrstaOdjeljenja",
        label: "Vrsta odjeljenja",
        dataFunc: (d: PaymentRequestStudentDetails) => (
          <Typography variant="body2">{d.sectionTypeName}</Typography>
        ),
      },
      {
        id: "transportCost",
        label: "Prijevoz",
        dataFunc: (d: PaymentRequestStudentDetails) => {
          return generateInfo(
            getTransportOptionInfoByStudentId(d.id, d.currency),
            d.numberOfDays,
            d.currency,
            d.transportCost,
            d.transportCostCorrected
          );
        },
      },
      {
        id: "foodCost",
        label: "Prehrana",
        dataFunc: (d: PaymentRequestStudentDetails) => {
          return generateInfo(
            getFoodOptionInfoByStudentId(d.id),
            d.numberOfDays,
            d.currency,
            d.foodCost,
            d.foodCostCorrected

          );
        },
      },
      {
        id: "teachingAidCost",
        label: "Posebna nastavna sredstva i pomagala",
        dataFunc: (d: PaymentRequestStudentDetails) => {
          return generateInfo(
            getTeachingAidOptionInfoByStudentId(
              d.id,
              d.programType,
              d.specialNeedType,
              d.currency,
              annualAdministration
            ),
            d.numberOfDays,
            d.currency,
            d.teachingAidCost,
            d.teachingAidCostCorrected
          );
        },
      },
      { id: "remark", label: "Napomena", dataName: "remark" },
    ];

  const tableBodyData: TrTableBodyData = {
    idDataName: "id",
    data: (paymentRequest && paymentRequest.studentDetails) || [],
  };

  const schoolCarrierTableHeadMetadata: TrTableHeadMetadata<
    PaymentRequestSchoolCarrierDetails
  >[] = [
      {
        id: "vehicleTypeName",
        label: "Vrsta vozila",
        dataName: "vehicleTypeName",
      },
      {
        id: "registrationPlate",
        label: "Registracijske oznake",
        dataName: "registrationPlate",
      },
      {
        id: "dailyMileageTotal",
        label: "Ukupna dnevna kilometraža",
        dataName: "dailyMileageTotal",
        dataFunc: (sc: PaymentRequestSchoolCarrierDetails) => {
          return numberFormat(sc.dailyMileageTotal, "km", 4);
        },
      },
      {
        id: "numberOfStudentsTotal",
        label: "Broj učenika",
        dataName: "numberOfStudentsTotal",
      },
      {
        id: "numberOfDays",
        label: "Broj dana",
        dataName: "numberOfDays",
      },
      {
        id: "transportCost",
        label: "Cijena",
        dataFunc: (d: PaymentRequestSchoolCarrierDetails) => {
          return `${currencyFormat(d.transportCost || 0, d.currency)} (${currencyFormat(
            d.transportCostCorrected || 0, d.currency
          )})`;
        },
      },
      {
        id: "remark",
        label: "Napomena",
        dataName: "remark",
      },
    ];

  const schoolCarrierTableBodyData: TrTableBodyData = {
    idDataName: "id",
    data: (paymentRequest && paymentRequest.schoolCarrierDetails) || [],
  };

  const authorizedCarrierTableHeadMetadata: TrTableHeadMetadata<
    PaymentRequestAuthorizedCarrierDetails
  >[] = [
      {
        id: "name",
        label: "Naziv prijevoznika",
        dataName: "name",
      },
      {
        id: "dailyMileage",
        label: "Ukupna dnevna kilometraža",
        dataName: "dailyMileage",
        dataFunc: (carrier: PaymentRequestAuthorizedCarrierDetails) => {
          return numberFormat(carrier.dailyMileage || 0, "km", 4);
        },
      },
      {
        id: "dailyPrice",
        label: "Cijena po danu",
        dataName: "dailyPrice",
        dataFunc: (carrier: PaymentRequestAuthorizedCarrierDetails) => {
          return currencyFormat(carrier.dailyPrice || 0, paymentRequest.currency);
        },
      },
      {
        id: "numberOfStudentsTotal",
        label: "Broj učenika",
        dataName: "numberOfStudentsTotal",
      },
      {
        id: "numberOfDays",
        label: "Broj dana",
        dataName: "numberOfDays",
      },
      {
        id: "transportCost",
        label: "Cijena",
        dataFunc: (d: PaymentRequestAuthorizedCarrierDetails) => {
          return `${currencyFormat(d.transportCost || 0, paymentRequest.currency)} (${currencyFormat(
            d.transportCostCorrected || 0, paymentRequest.currency
          )})`;
        },
      },
      {
        id: "remark",
        label: "Napomena",
        dataName: "remark",
      },
    ];

  const authorizedCarrierTableBodyData: TrTableBodyData = {
    idDataName: "id",
    data: (paymentRequest && paymentRequest.authorizedCarrierDetails) || [],
  };

  return paymentRequest.id ? (
    <div className={classes.root}>
    {location.pathname !== paymentRequestPrintUrl(id) ? (
      <Grid item xs={12}>
        <Button
          variant="outlined"
          color="primary"
          component={Link}
          to="/payment-requests"
          style={{ marginBottom: "16px" }}
        >
          {<ArrowBackIcon />}
        </Button>
      </Grid>
    ): null}


      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h5">
            Zahtjev za isplatom - {paymentRequest.schoolName}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Detalji zahtjeva"
              titleTypographyProps={{ variant: "h6" }}
              classes={{ title: classes.title }}
            />
            <Divider />
            <CardContent>
              <Grid container item xs={12} spacing={2}>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize" fontWeight="bold">
                    Školska godina
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize">
                    {paymentRequest.schoolYearName}
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize" fontWeight="bold">
                    Broj učenika
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize">
                    {paymentRequest.studentsNumber}
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize" fontWeight="bold">
                    Mjesec
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize">{new Date(paymentRequest.validFrom).toLocaleString('hr-hr', { month: 'long' })}</Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize" fontWeight="bold">
                    Iznos
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize">
                    {currencyFormat(paymentRequest.totalCost || 0, paymentRequest.currency)}

                  </Box>
                </Grid>
              </Grid>
              {
                // new Date(paymentRequest.validFrom).getFullYear() == 2023 
                // && new Date(paymentRequest.validFrom).getMonth() >= 1
                // && new Date(paymentRequest.validFrom).getMonth() <= 7
                isSpecialCalculationFor2023Date(paymentRequest.validFrom)
                  ?
                  <>
                    <Grid container item xs={12} spacing={2}>
                      <Grid item xs={12} md={2}>
                        <Box fontSize="body1.fontSize" fontWeight="bold">
                          Iznos od veljače 2023
                        </Box>
                      </Grid><Grid item xs={12} md={2}>
                        <Box fontSize="body1.fontSize">
                          {currencyFormat(paymentRequest.totalCostAfterFebruary2023 || 0, paymentRequest.currency)}
                        </Box>
                      </Grid>
                    </Grid></>
                  : ''
              }

              <Grid container item xs={12} spacing={2}>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize" fontWeight="bold">
                    Iznos s korekcijom
                  </Box>
                </Grid><Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize">
                    <Typography className={classes.resolvedCost}>
                      {currencyFormat(
                        (
                          // (new Date(paymentRequest.validFrom).getFullYear() >= 2023 
                          // && new Date(paymentRequest.validFrom).getMonth() >= 1
                          // && new Date(paymentRequest.validFrom).getMonth() <= 7
                          // ) 
                          isSpecialCalculationFor2023Date(paymentRequest.validFrom)
                            ?
                            paymentRequest.totalCostResolvedAfterFebruary2023 || 0 :
                            paymentRequest.totalCostResolved || 0), paymentRequest.currency)}
                    </Typography>
                  </Box>
                </Grid>

                {
                  isSpecialCalculationFor2023Date(paymentRequest.validFrom)
                    // new Date(paymentRequest.validFrom).getFullYear() >= 2023 

                    // && new Date(paymentRequest.validFrom).getMonth() >= 1
                    // && new Date(paymentRequest.validFrom).getMonth() <= 7
                    ?
                    <>
                      <Grid item xs={12} md={10} >
                        <Box fontSize="body1.fontSize">
                          <br></br>
                          Nova cijena primijenjuje se za sve mjesečne zahtjeve kreirane od veljače 2023. godine sukladno izmjeni Odluke o kriterijima za financiranje povećanih troškova prijevoza i posebnih nastavnih sredstava i pomagala te sufinanciranja prehrane učenika s teškoćama u razvoju u osnovnoškolskim programima za školsku godinu 2022./2023.
                        </Box>
                      </Grid>
                    </>
                    : ''
                }
              </Grid>
              <Grid
                container
                item
                xs={12}
                spacing={2}
                alignItems="center"
                justify="center"
              >
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize" fontWeight="bold">
                    Status zahtjeva
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize">
                    {
                      <PaymentRequestStatusElement
                        statusId={paymentRequest.statusId}
                        statusName={paymentRequest.statusName}
                      />
                    }
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize" fontWeight="bold">
                    Napomena
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box fontSize="body1.fontSize">
                    {paymentRequest.changeReason}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {paymentRequest.schoolCarrierDetails &&
        paymentRequest.schoolCarrierDetails.length > 0 ? (
        <Grid container spacing={2} className={"forPdf"}>
          <Grid item lg={12}>
            <Paper>
              <TrTable
                headMetadata={schoolCarrierTableHeadMetadata}
                bodyData={schoolCarrierTableBodyData}
              />
            </Paper>
          </Grid>
        </Grid>
      ) : null}
      {paymentRequest.authorizedCarrierDetails &&
        paymentRequest.authorizedCarrierDetails.length > 0 ? (
        <Grid container spacing={2} className={"forPdf"}>
          <Grid item lg={12}>
            <Paper>
              <TrTable
                headMetadata={authorizedCarrierTableHeadMetadata}
                bodyData={authorizedCarrierTableBodyData}
              />
            </Paper>
          </Grid>
        </Grid>
      ) : null}
      {paymentRequest.studentDetails &&
        paymentRequest.studentDetails.length > 0 ? (
        <Grid container spacing={2} className={"forPdf"}>
          <Grid item lg={12}>
            <Paper>
              <TrTable
                headMetadata={tableHeadMetadata}
                bodyData={tableBodyData}
              />
            </Paper>
          </Grid>
        </Grid>
      ) : null}

      {location.pathname !== paymentRequestPrintUrl(id) ? (
        <Grid container spacing={2}>
          <Grid item sm={12}>
            <Box textAlign="right">
              <Button
                variant="contained"
                color="primary"
                onClick={handleDownload}
              >
                <PictureAsPdf className={classes.icon} />
                Preuzmite zahtjev
              </Button>
            </Box>
          </Grid>
        </Grid>
      ) : null}
    </div>
  ) : null;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
    },
    title: {
      fontWeight: 400,
    },
    icon: {
      marginRight: theme.spacing(1),
    },
    resolvedCost: {
      color: theme.palette.error.main,
    },
  })
);
